import React, { useEffect, useState } from "react";
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Chip,
} from "@mui/material";
import apiClient from "../../../utils/apiClient";
import escapeHtml from "lodash/escape";
import Swal from "../../../utils/swal";
import { showSuccessToast, showErrorToast } from "../../../utils/swalToast";
import DashboardTitleComponent from "../../custom/DashboardTitleComponent";
import CustomDataGrid from "../../custom/CustomDataGrid";
import CustomButton from "../../custom/CustomButton";
import RowActionsMenu from "../../custom/RowActionsMenu";

const AccessRoleMenu = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    apiClient
      .get("/access-control/roles")
      .then((response) => setRoles(response.data.data || []))
      .catch((error) => console.error("Error fetching access roles", error))
      .finally(() => setLoading(false));
  }, [refresh]);

  const handleOpenAdd = () => {
    setIsEditMode(false);
    setSelectedId(null);
    setCode("");
    setName("");
    setOpenModal(true);
  };

  const handleOpenEdit = (role) => {
    setIsEditMode(true);
    setSelectedId(role.id);
    setCode(role.code);
    setName(role.name);
    setOpenModal(true);
  };

  const handleClose = () => setOpenModal(false);

  const handleSubmit = async () => {
    if (!isEditMode && !code.trim()) {
      return showErrorToast("Kode role wajib diisi");
    }
    if (!name.trim()) {
      return showErrorToast("Nama role wajib diisi");
    }

    try {
      if (isEditMode) {
        await apiClient.put(`/access-control/roles/${selectedId}`, { name });
      } else {
        await apiClient.post("/access-control/roles", { code, name });
      }
      showSuccessToast(`Role berhasil ${isEditMode ? "diperbaharui" : "ditambahkan"}`);
      setOpenModal(false);
      setRefresh((prev) => !prev);
    } catch (error) {
      showErrorToast(error?.response?.data?.msg || "Terjadi kesalahan");
    }
  };

  const handleDelete = async (role) => {
    const result = await Swal.fire({
      title: "Konfirmasi",
      html: `Hapus role <b>${escapeHtml(role.name)}</b>?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya",
      cancelButtonText: "Batal",
      confirmButtonColor: "#003158",
      cancelButtonColor: "#A92027",
    });
    if (!result.isConfirmed) return;

    try {
      const response = await apiClient.delete(`/access-control/roles/${role.id}`);
      showSuccessToast(response.data.msg || "Role berhasil dihapus");
      setRefresh((prev) => !prev);
    } catch (error) {
      showErrorToast(error?.response?.data?.msg || "Terjadi kesalahan");
    }
  };

  return (
    <div>
      <DashboardTitleComponent text={"Manajemen Role"} />
      <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
        <CustomButton
          onClick={handleOpenAdd}
          text="Tambah Role"
          isFullWidth={false}
          baseColor="#003158"
          hoverColor="#00213A"
        />
      </div>
      <CustomDataGrid
        rows={roles.map((role) => ({ ...role }))}
        loading={loading}
        columns={[
          { field: "id", headerName: "ID", width: 80 },
          { field: "code", headerName: "Kode", flex: 1 },
          { field: "name", headerName: "Nama", flex: 1 },
          {
            field: "is_system",
            headerName: "Tipe",
            flex: 1,
            renderCell: (params) => (
              <Chip
                label={params.value ? "Sistem" : "Custom"}
                size="small"
                color={params.value ? "default" : "primary"}
              />
            ),
          },
          {
            field: "action",
            headerName: "Action",
            flex: 1,
            renderCell: (params) =>
              params.row.is_system ? (
                <Chip label="Terkunci" size="small" />
              ) : (
                <RowActionsMenu
                  actions={[
                    { label: "Edit", onClick: () => handleOpenEdit(params.row) },
                    { label: "Hapus", onClick: () => handleDelete(params.row), danger: true },
                  ]}
                />
              ),
          },
        ]}
      />
      <Dialog open={openModal} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{isEditMode ? "Edit Role" : "Tambah Role"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Kode Role"
                fullWidth
                value={code}
                disabled={isEditMode}
                onChange={(e) => setCode(e.target.value)}
                helperText="Contoh: koordinator_wilayah (tidak bisa diubah setelah dibuat)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Nama Role"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomButton
                onClick={handleSubmit}
                text={isEditMode ? "Update" : "Tambah"}
                isFullWidth={false}
                baseColor="#003158"
                hoverColor="#00213A"
              />
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccessRoleMenu;
